import React, { useEffect, useRef, useState } from 'react';
import { clearUser } from '../../utils/FRLogout';
import { Button, Modal } from 'react-bootstrap';
import { ABSOLUTE_LIMIT_MS, ABSOLUTE_WARNING_BEFORE_MS, IDLE_LIMIT_MS, IDLE_POPUP_GRACE_MS, SESSION_START_KEY, LAST_ACTIVITY_KEY, LOGOUT_BROADCAST_KEY } from '../../constants/SessionTimer';

const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

const SessionTimeoutManager = () => {
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [showAbsoluteWarning, setShowAbsoluteWarning] = useState(false);

  const idleWarningTimerRef = useRef(null);
  const idleAutoLogoutTimerRef = useRef(null);
  const absoluteWarningTimerRef = useRef(null);
  const absoluteLogoutTimerRef = useRef(null);
  const hasAbsoluteTimersBeenSet = useRef(false);
  let isLoggingOut = false;

  const safeLogout = () => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    clearTimeout(absoluteWarningTimerRef.current);
    clearTimeout(absoluteLogoutTimerRef.current);

    absoluteWarningTimerRef.current = null;
    absoluteLogoutTimerRef.current = null;

    console.log('[ABS] Logging out once only');
    handleLogout();
  };

  // Track if idle warning is visible (for event handlers)
  const idleWarningActiveRef = useRef(false);
  useEffect(() => {
    idleWarningActiveRef.current = showIdleWarning;
  }, [showIdleWarning]);

  const clearAllTimers = () => {
    if (idleWarningTimerRef.current) clearTimeout(idleWarningTimerRef.current);
    if (idleAutoLogoutTimerRef.current) clearTimeout(idleAutoLogoutTimerRef.current);
    if (absoluteWarningTimerRef.current) clearTimeout(absoluteWarningTimerRef.current);
    if (absoluteLogoutTimerRef.current) clearTimeout(absoluteLogoutTimerRef.current);

    idleWarningTimerRef.current = null;
    idleAutoLogoutTimerRef.current = null;
    absoluteWarningTimerRef.current = null;
    absoluteLogoutTimerRef.current = null;
  };

  const broadcastLogout = () => {
    try {
      // writing a unique value triggers storage event in other tabs
      localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
    } catch (e) {
      console.error('Failed to broadcast logout', e);
    }
  };

  const handleLogout = () => {
    setShowIdleWarning(false);
    clearAllTimers();
    console.log('[IDL] clear all timers')
    broadcastLogout();
    console.log('[IDL] broadcasting logout')
    clearUser();
    setTimeout(() => {
      window.location.reload(true);
    }, 100);
  };

  /**
   * Reset idle timers for *this tab* only.
   */
  const resetIdleTimer = () => {
    if (idleWarningTimerRef.current) clearTimeout(idleWarningTimerRef.current);
    if (idleAutoLogoutTimerRef.current) clearTimeout(idleAutoLogoutTimerRef.current);

    idleWarningTimerRef.current = null;
    idleAutoLogoutTimerRef.current = null;

    const idleWarningDelay = Math.max(IDLE_LIMIT_MS - IDLE_POPUP_GRACE_MS, 0);

    idleWarningTimerRef.current = setTimeout(() => {
      console.log('set to idle warning mode');
      setShowIdleWarning(true);

      // After GRACE time, log out if user didn’t click "Stay Logged In"
      idleAutoLogoutTimerRef.current = setTimeout(() => {
        handleLogout();
      }, IDLE_POPUP_GRACE_MS);
    }, idleWarningDelay);
  };

  /**
   * Broadcast last activity to other tabs and reset idle timers locally.
   */
  const recordActivityAndResetIdle = () => {
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    } catch (e) {
      console.error('[IDL] Failed to record last activity', e);
    }
    resetIdleTimer();
  };

  // Any user activity resets idle timer (when idle warning is not active)
  const handleUserActivity = () => {

    // Once the idle popup is shown, ignore activity in that tab
    // User must explicitly click "Stay Logged In" or "Log Out"
    if (idleWarningActiveRef.current) {
      console.log('Ignoring activity because idle warning is shown');
      return;
    }

    recordActivityAndResetIdle();
  };

  // --------- ABSOLUTE TIMEOUT (10 hours - 5 min warning) ----------
  const initAbsoluteSessionTimers = () => {
    if (hasAbsoluteTimersBeenSet.current) {
      console.log("[ABS] Timers already initialized → skip");
      return;
    }
    hasAbsoluteTimersBeenSet.current = true;
    const now = Date.now();
    let sessionStart = Number(localStorage.getItem(SESSION_START_KEY));

    if (!sessionStart || Number.isNaN(sessionStart)) {
      // First time we see this session :- fix the start time
      sessionStart = now;
      localStorage.setItem(SESSION_START_KEY, sessionStart.toString());
      console.log('[ABS] Setting initial SESSION_START_TIME =', new Date(sessionStart).toISOString());
    } else {
      console.log('[ABS] Using existing SESSION_START_TIME =', new Date(sessionStart).toISOString());
    }

    const elapsed = now - sessionStart;
    const warningDelay = ABSOLUTE_LIMIT_MS - ABSOLUTE_WARNING_BEFORE_MS - elapsed;
    const logoutDelay = ABSOLUTE_LIMIT_MS - elapsed;

    console.log('[ABS] elapsed(ms)=', elapsed, 'warningDelay=', warningDelay, 'logoutDelay=', logoutDelay);

    // If somehow already beyond limit, force logout
    if (logoutDelay <= 0) {
      console.log('[ABS] Already beyond absolute limit -> logging out');
      safeLogout();
      return;
    }

    // Clear any existing absolute timers before setting new ones
    if (absoluteWarningTimerRef.current) clearTimeout(absoluteWarningTimerRef.current);
    if (absoluteLogoutTimerRef.current) clearTimeout(absoluteLogoutTimerRef.current);
    absoluteWarningTimerRef.current = null;
    absoluteLogoutTimerRef.current = null;

    // Warning timer (5 minutes before absolute limit)
    if (warningDelay > 0) {
      absoluteWarningTimerRef.current = setTimeout(() => {
        console.log('[ABS] Showing absolute warning popup');
        setShowAbsoluteWarning(true);
      }, warningDelay);
    } else {
      // Already inside last 5 minutes → show immediately
      console.log('[ABS] In last 5 minutes, showing warning immediately');
      setShowAbsoluteWarning(true);
    }

    // Hard logout timer at absolute limit
    absoluteLogoutTimerRef.current = setTimeout(() => {
      console.log('[ABS] Absolute timeout reached, logging out');
      safeLogout();
    }, logoutDelay);
  };


  // Initialise listeners & timers on mount
  useEffect(() => {
    // local activity listeners
    activityEvents.forEach((ev) => window.addEventListener(ev, handleUserActivity));

    // initial idle timers
    resetIdleTimer();

    // absolute timeout timers
    initAbsoluteSessionTimers();

    // storage listener (for multi-tab)
    const handleStorage = (event) => {
      const { key, newValue } = event;
      if (!key) return;

      if (key === LOGOUT_BROADCAST_KEY && newValue) {
        console.log('Received logout broadcast from another tab');
        clearAllTimers();
        clearUser();
      }

      if (key === LAST_ACTIVITY_KEY && newValue) {
        console.log('Received activity from another tab');
        if (!idleWarningActiveRef.current) {
          resetIdleTimer();
        }
      }

      if (key === SESSION_START_KEY && newValue) {
        console.log('SESSION_START_TIME changed, re-init absolute timers');
        initAbsoluteSessionTimers();
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      activityEvents.forEach((ev) => window.removeEventListener(ev, handleUserActivity));
      window.removeEventListener('storage', handleStorage);
      clearAllTimers();
    };
  }, []);


  const handleStayLoggedIn = async () => {
    try {
      console.log('refresh token');
      setShowIdleWarning(false);

      // Stop pending auto-logout while we renew the token
      if (idleAutoLogoutTimerRef.current) {
        clearTimeout(idleAutoLogoutTimerRef.current);
        idleAutoLogoutTimerRef.current = null;
      }
      // User chose to continue: broadcast activity + restart idle timer in all tabs
      recordActivityAndResetIdle();
    } catch (err) {
      console.error('Reauthentication during idle keep-alive failed', err);
    }
  };

  const handleAbsoluteOk = () => {
    // Just close popup, logout timer still runs in background
    setShowAbsoluteWarning(false);
  };


  return (
    <>
      {/* Idle Warning Modal */}
      <Modal
        className="sessionIdleModal"
        show={showIdleWarning}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Session Timeout Warning</Modal.Title>
        </Modal.Header>

        <div>
          <Modal.Body style={{ minHeight: '100px' }}>
            <p>Your session is about to expire due to inactivity.</p>
          </Modal.Body>
          <Modal.Footer>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <Button bsStyle="success" onClick={handleStayLoggedIn}>
                Stay Logged In
              </Button>
              <Button onClick={safeLogout}>
                Log Out
              </Button>
            </div>
          </Modal.Footer>
        </div>
      </Modal>

      {/* Absolute Timeout Warning Modal */}
      <Modal
        className="sessionAbsoluteModal"
        show={showAbsoluteWarning}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Session Expiring Soon</Modal.Title>
        </Modal.Header>

        <div>
          <Modal.Body style={{ minHeight: '100px' }}>
            <p>Your session will expire in 5 minutes. Please save your work and log in again to continue.</p>
          </Modal.Body>

          <Modal.Footer>
            <div style={{ width: '100%', textAlign: 'right' }}>
              <Button onClick={handleAbsoluteOk}>OK</Button>
            </div>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
};

export default SessionTimeoutManager;


